import React, { useState, useEffect, useCallback } from 'react'
import {
    FileDown, Filter, TrendingUp, ChevronDown, ChevronRight,
    DollarSign, IndianRupee, Users, ShoppingBag, TrendingDown,
    List
} from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import SCREEN_IDS from '../../utils/screenIds'
import * as XLSX from 'xlsx'

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = new Date()
const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`
const todayStr = today.toISOString().split('T')[0]

const fmtNum = (n) => parseFloat(n || 0).toLocaleString('en-IN')
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—'

// ── Component ─────────────────────────────────────────────────────────────────

const SalesReport = () => {
    // ── Tab state ──────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('by-vendor')

    // ── By-Vendor tab state ────────────────────────────────────────────────────
    const [vendors, setVendors] = useState([])
    const [totals, setTotals] = useState(null)
    const [loading, setLoading] = useState(true)
    const [expandedRows, setExpandedRows] = useState(new Set())

    const [filters, setFilters] = useState({
        from: firstOfMonth,
        to: todayStr,
        currency: 'all'
    })

    // ── By-Item tab state ──────────────────────────────────────────────────────
    const [items, setItems] = useState([])
    const [itemTotals, setItemTotals] = useState(null)
    const [itemLoading, setItemLoading] = useState(false)
    const [vendorList, setVendorList] = useState([])

    const [itemFilters, setItemFilters] = useState({
        from: firstOfMonth,
        to: todayStr,
        currency: 'all',
        vendor_id: 'all',
        search: ''
    })

    // ── Fetch vendor list (for By-Item filter dropdown) ────────────────────────
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const res = await fetch('/api/vendors', { credentials: 'include' })
                const data = await res.json()
                if (data.success) setVendorList(data.data || data.vendors || [])
                else if (Array.isArray(data)) setVendorList(data)
            } catch (err) {
                console.error('Error fetching vendor list:', err)
            }
        }
        fetchVendors()
    }, [])

    // ── Fetch By-Vendor data ───────────────────────────────────────────────────
    const fetchReport = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams()
            if (filters.from) params.append('from', filters.from)
            if (filters.to) params.append('to', filters.to)
            if (filters.currency && filters.currency !== 'all') params.append('currency', filters.currency)

            const res = await fetch(`/api/reports/sales-by-vendor?${params}`, { credentials: 'include' })
            const data = await res.json()
            if (data.success) {
                setVendors(data.data.vendors)
                setTotals(data.data.totals)
            }
        } catch (err) {
            console.error('Error fetching vendor sales report:', err)
        } finally {
            setLoading(false)
        }
    }, [filters])

    useEffect(() => { fetchReport() }, [fetchReport])

    // ── Fetch By-Item data ─────────────────────────────────────────────────────
    const fetchItems = useCallback(async () => {
        setItemLoading(true)
        try {
            const params = new URLSearchParams()
            if (itemFilters.from) params.append('from', itemFilters.from)
            if (itemFilters.to) params.append('to', itemFilters.to)
            if (itemFilters.currency && itemFilters.currency !== 'all') params.append('currency', itemFilters.currency)
            if (itemFilters.vendor_id && itemFilters.vendor_id !== 'all') params.append('vendor_id', itemFilters.vendor_id)
            if (itemFilters.search && itemFilters.search.trim()) params.append('search', itemFilters.search.trim())

            const res = await fetch(`/api/reports/sales-by-item?${params}`, { credentials: 'include' })
            const data = await res.json()
            if (data.success) {
                setItems(data.data.items)
                setItemTotals(data.data.totals)
            }
        } catch (err) {
            console.error('Error fetching item sales report:', err)
        } finally {
            setItemLoading(false)
        }
    }, [itemFilters])

    useEffect(() => {
        if (activeTab === 'by-item') fetchItems()
    }, [activeTab, fetchItems])

    // ── Filter handlers ────────────────────────────────────────────────────────
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }))
        setExpandedRows(new Set())
    }

    const handleItemFilterChange = (key, value) => {
        setItemFilters(prev => ({ ...prev, [key]: value }))
    }

    // ── Vendor-row expand/collapse ─────────────────────────────────────────────
    const toggleRow = (key) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            if (next.has(key)) { next.delete(key) } else { next.add(key) }
            return next
        })
    }
    const expandAll = () => {
        setExpandedRows(new Set(vendors.map(v => `${v.vendor_id}|${v.currency}`)))
    }
    const collapseAll = () => setExpandedRows(new Set())

    // ── Drill-through: vendor row → By Item tab pre-filtered ──────────────────
    const drillToItems = (vendor) => {
        setItemFilters(prev => ({
            ...prev,
            from: filters.from,
            to: filters.to,
            currency: filters.currency,
            vendor_id: String(vendor.vendor_id)
        }))
        setActiveTab('by-item')
    }

    // ── Exports ────────────────────────────────────────────────────────────────
    const handleExportVendor = () => {
        if (!vendors.length) return
        const wb = XLSX.utils.book_new()

        const summaryRows = vendors.map(v => ({
            'Vendor': v.vendor_code || v.vendor_name,
            'Currency': v.currency,
            'Items Sold': parseInt(v.item_count),
            'Sale Price': parseFloat(v.sale_price),
            'Sale Customs': parseFloat(v.sale_customs),
            'Cost Price': parseFloat(v.cost_price),
            'Cost Customs': parseFloat(v.cost_customs),
            'Profit / Loss': parseFloat(v.profit),
            'Exhibition(s)': v.exhibition_names || ''
        }))
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'By Vendor')

        const allItems = vendors.flatMap(v =>
            (v.items || []).map(item => ({
                'Vendor': v.vendor_code || v.vendor_name,
                'Item Code': item.item_code,
                'Category': item.category || '',
                'Purity': item.gold_purity ? `${item.gold_purity}KT` : '',
                'Gross Wt (g)': parseFloat(item.gross_weight || 0).toFixed(3),
                'Net Wt (g)': parseFloat(item.net_weight || 0).toFixed(3),
                'Bill No.': item.sold_bill_number || '',
                'Sale Date': fmtDate(item.effective_date),
                'Exhibition': item.exhibition_name || '',
                'Customer': item.customer_name || '',
                'Currency': item.currency,
                'Sale Price': parseFloat(item.sale_price || 0),
                'Sale Customs': parseFloat(item.sale_customs || 0),
                'Cost Price': parseFloat(item.cost_price || 0),
                'Cost Customs': parseFloat(item.cost_customs || 0),
                'Profit / Loss': parseFloat(item.profit || 0)
            }))
        )
        if (allItems.length) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allItems), 'All Items')
        }
        XLSX.writeFile(wb, `Sales_By_Vendor_${todayStr}.xlsx`)
    }

    const handleExportItems = () => {
        if (!items.length) return
        const wb = XLSX.utils.book_new()
        const rows = items.map(item => ({
            'Item Code': item.item_code,
            'Category': item.category || '',
            'Vendor': item.vendor_code || '',
            'Exhibition': item.exhibition_name || '',
            'Sale Date': fmtDate(item.effective_date),
            'Customer': item.customer_name || '',
            'Bill No.': item.sold_bill_number || '',
            'Currency': item.currency,
            'Purity': item.gold_purity ? `${item.gold_purity}KT` : '',
            'Gross Wt (g)': parseFloat(item.gross_weight || 0).toFixed(3),
            'Net Wt (g)': parseFloat(item.net_weight || 0).toFixed(3),
            'Sale Price': parseFloat(item.sale_price || 0),
            'Sale Customs': parseFloat(item.sale_customs || 0),
            'Cost Price': parseFloat(item.cost_price || 0),
            'Cost Customs': parseFloat(item.cost_customs || 0),
            'Profit / Loss': parseFloat(item.profit || 0)
        }))
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'By Item')
        XLSX.writeFile(wb, `Sales_By_Item_${todayStr}.xlsx`)
    }

    // ── Render helpers ──────────────────────────────────────────────────────────
    const ProfitBadge = ({ value }) => {
        const num = parseFloat(value) || 0
        if (num > 0) return (
            <span className="flex items-center gap-1 text-green-700 font-bold">
                <TrendingUp size={13} />{fmtNum(num)}
            </span>
        )
        if (num < 0) return (
            <span className="flex items-center gap-1 text-red-600 font-bold">
                <TrendingDown size={13} />{fmtNum(num)}
            </span>
        )
        return <span className="text-gray-400 font-medium">—</span>
    }

    const currencyIcon = (c) => c === 'USD'
        ? <DollarSign size={12} className="text-green-600" />
        : <IndianRupee size={12} className="text-orange-600" />

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <PageIdentifier
                pageId={activeTab === 'by-item' ? SCREEN_IDS.REPORTS.SALES_BY_ITEM : SCREEN_IDS.REPORTS.SALES_BY_VENDOR}
                pageName={activeTab === 'by-item' ? 'Sales Report by Item' : 'Sales Report by Vendor'}
            />

            {/* ── Page header ── */}
            <div className="flex flex-wrap justify-between items-start gap-4 mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        {activeTab === 'by-item'
                            ? <><List className="text-amber-600" size={28} />Sales Report by Item</>
                            : <><Users className="text-amber-600" size={28} />Sales Report by Vendor</>}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {activeTab === 'by-item'
                            ? 'Flat item-level sales, costs & profit'
                            : 'Aggregated sales, costs & profit per vendor'}
                    </p>
                </div>
                <button
                    onClick={activeTab === 'by-item' ? handleExportItems : handleExportVendor}
                    disabled={activeTab === 'by-item' ? !items.length : !vendors.length}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                >
                    <FileDown size={16} />
                    Export to Excel
                </button>
            </div>

            {/* ── Tab bar ── */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setActiveTab('by-vendor')}
                    className={`px-6 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'by-vendor'
                            ? 'border-amber-500 text-amber-700 bg-amber-50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                >
                    <Users size={14} className="inline mr-1.5 mb-0.5" />
                    By Vendor
                </button>
                <button
                    onClick={() => setActiveTab('by-item')}
                    className={`px-6 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'by-item'
                            ? 'border-amber-500 text-amber-700 bg-amber-50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                >
                    <List size={14} className="inline mr-1.5 mb-0.5" />
                    By Item
                </button>
            </div>

            {/* ════════════════════════════════════════════════════════════════════
          TAB 1 — BY VENDOR
      ════════════════════════════════════════════════════════════════════ */}
            {activeTab === 'by-vendor' && (
                <>
                    {/* ── Filters ── */}
                    <div className="bg-white rounded-lg shadow p-4 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Filter size={16} className="text-gray-500" />
                            <h2 className="text-sm font-semibold text-gray-700">Filters</h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
                                <input
                                    type="date"
                                    value={filters.from}
                                    onChange={e => handleFilterChange('from', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
                                <input
                                    type="date"
                                    value={filters.to}
                                    onChange={e => handleFilterChange('to', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                                <select
                                    value={filters.currency}
                                    onChange={e => handleFilterChange('currency', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                >
                                    <option value="all">All Currencies</option>
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        setFilters({ from: firstOfMonth, to: todayStr, currency: 'all' })
                                        setExpandedRows(new Set())
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Summary cards ── */}
                    {totals && !loading && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Items Sold</p>
                                <p className="text-2xl font-bold text-gray-800">{fmtNum(totals.item_count)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Sale Price</p>
                                <p className="text-lg font-bold text-blue-700">{fmtNum(totals.sale_price)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Sale Customs</p>
                                <p className="text-lg font-bold text-blue-500">{fmtNum(totals.sale_customs)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Cost Price</p>
                                <p className="text-lg font-bold text-orange-600">{fmtNum(totals.cost_price)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Cost Customs</p>
                                <p className="text-lg font-bold text-orange-400">{fmtNum(totals.cost_customs)}</p>
                            </div>
                            <div className={`rounded-lg shadow p-4 ${parseFloat(totals.profit) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                                <p className="text-xs text-gray-500">Total Profit / Loss</p>
                                <p className={`text-lg font-bold ${parseFloat(totals.profit) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                    {fmtNum(totals.profit)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── Vendor table ── */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {!loading && vendors.length > 0 && (
                            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
                                <p className="text-xs text-gray-500">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</p>
                                <div className="flex gap-2">
                                    <button onClick={expandAll} className="text-xs text-blue-600 hover:underline">Expand All</button>
                                    <span className="text-gray-300">|</span>
                                    <button onClick={collapseAll} className="text-xs text-blue-600 hover:underline">Collapse All</button>
                                </div>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="text-center py-14">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
                                    <p className="mt-2 text-gray-500 text-sm">Loading vendor sales…</p>
                                </div>
                            ) : vendors.length === 0 ? (
                                <div className="text-center py-16">
                                    <ShoppingBag className="mx-auto h-12 w-12 text-gray-300" />
                                    <p className="mt-3 text-gray-500 font-medium">No sales found</p>
                                    <p className="text-xs text-gray-400 mt-1">Try adjusting the date range or currency filter</p>
                                </div>
                            ) : (
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="w-8 px-3 py-3"></th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Currency</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Items</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase tracking-wider">Sale Price</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-blue-400 uppercase tracking-wider">Sale Customs</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-orange-600 uppercase tracking-wider">Cost Price</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-orange-400 uppercase tracking-wider">Cost Customs</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Profit / Loss</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vendors.map(vendor => {
                                            const rowKey = `${vendor.vendor_id}|${vendor.currency}`
                                            const isExpanded = expandedRows.has(rowKey)
                                            const vendorItems = vendor.items || []

                                            return (
                                                <React.Fragment key={rowKey}>
                                                    <tr
                                                        className={`border-b border-gray-100 cursor-pointer transition-colors ${isExpanded ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                                                        onClick={() => toggleRow(rowKey)}
                                                    >
                                                        <td className="px-3 py-3 text-gray-400">
                                                            {vendorItems.length > 0
                                                                ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)
                                                                : <span className="w-4 inline-block" />}
                                                        </td>
                                                        <td className="px-4 py-3 font-semibold text-gray-900">
                                                            <div className="flex flex-col">
                                                                <span>{vendor.vendor_code || vendor.vendor_name}</span>
                                                                {vendor.exhibition_names && (
                                                                    <span className="text-xs text-amber-600 font-normal mt-0.5">
                                                                        🏛 {vendor.exhibition_names}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="flex items-center gap-1 font-medium text-gray-600">
                                                                {currencyIcon(vendor.currency)}{vendor.currency}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-medium text-gray-700">{vendor.item_count}</td>
                                                        <td className="px-4 py-3 text-right font-bold text-blue-700">{fmtNum(vendor.sale_price)}</td>
                                                        <td className="px-4 py-3 text-right text-blue-500">{fmtNum(vendor.sale_customs)}</td>
                                                        <td className="px-4 py-3 text-right font-medium text-orange-600">{fmtNum(vendor.cost_price)}</td>
                                                        <td className="px-4 py-3 text-right text-orange-400">{fmtNum(vendor.cost_customs)}</td>
                                                        <td className="px-4 py-3 text-right"><ProfitBadge value={vendor.profit} /></td>
                                                    </tr>

                                                    {/* Expandable item sub-table */}
                                                    {isExpanded && vendorItems.length > 0 && (
                                                        <tr>
                                                            <td colSpan={9} className="p-0 bg-amber-50 border-b border-amber-200">
                                                                <div className="overflow-x-auto">
                                                                    <table className="min-w-full text-xs">
                                                                        <thead>
                                                                            <tr className="bg-amber-100 text-amber-800">
                                                                                <th className="px-4 py-2 text-left font-semibold">Item Code</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Category</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Purity</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Gross Wt</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Bill No.</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Sale Date</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Exhibition</th>
                                                                                <th className="px-4 py-2 text-left font-semibold">Customer</th>
                                                                                <th className="px-4 py-2 text-right font-semibold">Sale Price</th>
                                                                                <th className="px-4 py-2 text-right font-semibold">Cost Price</th>
                                                                                <th className="px-4 py-2 text-right font-semibold">Profit</th>
                                                                                <th className="px-4 py-2"></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {vendorItems.map(item => (
                                                                                <tr key={item.id} className="border-t border-amber-100 hover:bg-amber-50">
                                                                                    <td className="px-4 py-2 font-mono font-semibold text-gray-800">{item.item_code}</td>
                                                                                    <td className="px-4 py-2 text-gray-600">{item.category || '—'}</td>
                                                                                    <td className="px-4 py-2">
                                                                                        {item.gold_purity
                                                                                            ? <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded font-semibold">{item.gold_purity}KT</span>
                                                                                            : '—'}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-gray-600">{parseFloat(item.gross_weight || 0).toFixed(2)}g</td>
                                                                                    <td className="px-4 py-2 text-blue-700 font-medium">{item.sold_bill_number || '—'}</td>
                                                                                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDate(item.effective_date)}</td>
                                                                                    <td className="px-4 py-2 text-amber-700">{item.exhibition_name || <span className="text-gray-300">—</span>}</td>
                                                                                    <td className="px-4 py-2 text-gray-700">{item.customer_name || '—'}</td>
                                                                                    <td className="px-4 py-2 text-right font-bold text-blue-700">{fmtNum(item.sale_price)}</td>
                                                                                    <td className="px-4 py-2 text-right text-orange-600">{fmtNum(item.cost_price)}</td>
                                                                                    <td className="px-4 py-2 text-right"><ProfitBadge value={item.profit} /></td>
                                                                                    <td className="px-4 py-2">
                                                                                        <button
                                                                                            title="View all items for this vendor"
                                                                                            onClick={e => { e.stopPropagation(); drillToItems(vendor) }}
                                                                                            className="text-xs text-amber-600 hover:underline whitespace-nowrap"
                                                                                        >
                                                                                            See all →
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {isExpanded && vendorItems.length === 0 && (
                                                        <tr>
                                                            <td colSpan={9} className="px-8 py-3 text-xs text-gray-400 bg-amber-50 border-b border-amber-100">
                                                                No item-level detail available for this vendor.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}

                                        {/* Grand total row */}
                                        {totals && (
                                            <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold text-sm">
                                                <td className="px-3 py-3"></td>
                                                <td className="px-4 py-3 text-gray-700" colSpan={2}>Grand Total</td>
                                                <td className="px-4 py-3 text-right text-gray-700">{fmtNum(totals.item_count)}</td>
                                                <td className="px-4 py-3 text-right text-blue-700">{fmtNum(totals.sale_price)}</td>
                                                <td className="px-4 py-3 text-right text-blue-500">{fmtNum(totals.sale_customs)}</td>
                                                <td className="px-4 py-3 text-right text-orange-600">{fmtNum(totals.cost_price)}</td>
                                                <td className="px-4 py-3 text-right text-orange-400">{fmtNum(totals.cost_customs)}</td>
                                                <td className="px-4 py-3 text-right"><ProfitBadge value={totals.profit} /></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {!loading && vendors.length > 0 && (
                        <p className="mt-2 text-xs text-gray-400 text-right">
                            {vendors.length} vendor row{vendors.length !== 1 ? 's' : ''} · Click row to expand items · Click "See all →" to jump to By Item tab
                        </p>
                    )}
                </>
            )}

            {/* ════════════════════════════════════════════════════════════════════
          TAB 2 — BY ITEM
      ════════════════════════════════════════════════════════════════════ */}
            {activeTab === 'by-item' && (
                <>
                    {/* ── Filters ── */}
                    <div className="bg-white rounded-lg shadow p-4 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Filter size={16} className="text-gray-500" />
                            <h2 className="text-sm font-semibold text-gray-700">Filters</h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
                                <input
                                    type="date"
                                    value={itemFilters.from}
                                    onChange={e => handleItemFilterChange('from', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
                                <input
                                    type="date"
                                    value={itemFilters.to}
                                    onChange={e => handleItemFilterChange('to', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                                <select
                                    value={itemFilters.currency}
                                    onChange={e => handleItemFilterChange('currency', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                >
                                    <option value="all">All Currencies</option>
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Vendor</label>
                                <select
                                    value={itemFilters.vendor_id}
                                    onChange={e => handleItemFilterChange('vendor_id', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                >
                                    <option value="all">All Vendors</option>
                                    {vendorList.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                                <input
                                    type="text"
                                    placeholder="Item code / bill / customer"
                                    value={itemFilters.search}
                                    onChange={e => handleItemFilterChange('search', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>
                            <div className="flex items-end gap-2">
                                <button
                                    onClick={() => fetchItems()}
                                    className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 font-medium"
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={() => setItemFilters({ from: firstOfMonth, to: todayStr, currency: 'all', vendor_id: 'all', search: '' })}
                                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Summary cards ── */}
                    {itemTotals && !itemLoading && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Items</p>
                                <p className="text-2xl font-bold text-gray-800">{fmtNum(itemTotals.item_count)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Sale Price</p>
                                <p className="text-lg font-bold text-blue-700">{fmtNum(itemTotals.sale_price)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Sale Customs</p>
                                <p className="text-lg font-bold text-blue-500">{fmtNum(itemTotals.sale_customs)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Cost Price</p>
                                <p className="text-lg font-bold text-orange-600">{fmtNum(itemTotals.cost_price)}</p>
                            </div>
                            <div className="bg-white rounded-lg shadow p-4">
                                <p className="text-xs text-gray-500">Cost Customs</p>
                                <p className="text-lg font-bold text-orange-400">{fmtNum(itemTotals.cost_customs)}</p>
                            </div>
                            <div className={`rounded-lg shadow p-4 ${parseFloat(itemTotals.profit) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                                <p className="text-xs text-gray-500">Total Profit / Loss</p>
                                <p className={`text-lg font-bold ${parseFloat(itemTotals.profit) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                    {fmtNum(itemTotals.profit)}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── Flat item table ── */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {!itemLoading && items.length > 0 && (
                            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                                <p className="text-xs text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            {itemLoading ? (
                                <div className="text-center py-14">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
                                    <p className="mt-2 text-gray-500 text-sm">Loading item sales…</p>
                                </div>
                            ) : items.length === 0 ? (
                                <div className="text-center py-16">
                                    <ShoppingBag className="mx-auto h-12 w-12 text-gray-300" />
                                    <p className="mt-3 text-gray-500 font-medium">No items found</p>
                                    <p className="text-xs text-gray-400 mt-1">Try adjusting the filters above</p>
                                </div>
                            ) : (
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Item Code</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Exhibition</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Sale Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Currency</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase tracking-wider">Sale Price</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-blue-400 uppercase tracking-wider">Sale Customs</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-orange-600 uppercase tracking-wider">Cost Price</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-orange-400 uppercase tracking-wider">Cost Customs</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Profit / Loss</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, idx) => (
                                            <tr key={item.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="px-4 py-3 font-mono font-semibold text-gray-800">{item.item_code}</td>
                                                <td className="px-4 py-3 text-gray-600">{item.category || '—'}</td>
                                                <td className="px-4 py-3 text-gray-700">{item.vendor_code || '—'}</td>
                                                <td className="px-4 py-3 text-amber-700">
                                                    {item.exhibition_name || <span className="text-gray-300">—</span>}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(item.effective_date)}</td>
                                                <td className="px-4 py-3 text-gray-700">{item.customer_name || '—'}</td>
                                                <td className="px-4 py-3">
                                                    <span className="flex items-center gap-1 text-gray-600 font-medium">
                                                        {currencyIcon(item.currency)}{item.currency}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right font-bold text-blue-700">{fmtNum(item.sale_price)}</td>
                                                <td className="px-4 py-3 text-right text-blue-500">{fmtNum(item.sale_customs)}</td>
                                                <td className="px-4 py-3 text-right font-medium text-orange-600">{fmtNum(item.cost_price)}</td>
                                                <td className="px-4 py-3 text-right text-orange-400">{fmtNum(item.cost_customs)}</td>
                                                <td className="px-4 py-3 text-right"><ProfitBadge value={item.profit} /></td>
                                            </tr>
                                        ))}

                                        {/* Grand total footer */}
                                        {itemTotals && (
                                            <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold text-sm">
                                                <td className="px-4 py-3 text-gray-700" colSpan={7}>Grand Total ({fmtNum(itemTotals.item_count)} items)</td>
                                                <td className="px-4 py-3 text-right text-blue-700">{fmtNum(itemTotals.sale_price)}</td>
                                                <td className="px-4 py-3 text-right text-blue-500">{fmtNum(itemTotals.sale_customs)}</td>
                                                <td className="px-4 py-3 text-right text-orange-600">{fmtNum(itemTotals.cost_price)}</td>
                                                <td className="px-4 py-3 text-right text-orange-400">{fmtNum(itemTotals.cost_customs)}</td>
                                                <td className="px-4 py-3 text-right"><ProfitBadge value={itemTotals.profit} /></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {!itemLoading && items.length > 0 && (
                        <p className="mt-2 text-xs text-gray-400 text-right">
                            {items.length} item{items.length !== 1 ? 's' : ''} shown · Sorted by sale date descending
                        </p>
                    )}
                </>
            )}
        </div>
    )
}

export default SalesReport
